/**
 * SettingsTab.tsx - Settings management tab for Admin Dashboard
 * 
 * Features:
 * - Discount codes CRUD management
 * - App configuration (future)
 * - Inventory thresholds (future)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import PushNotificationsTab from './PushNotificationsTab';
import './SettingsTab.css';

// Types matching backend API
interface DiscountCode {
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  minOrderValue?: number;
  maxUses?: number;
  usedCount: number;
  isActive: boolean;
  expiresAt?: string;
  eligibilityCriteria?: {
    requiresSampleDelivered?: boolean;
    requiresFirstPurchase?: boolean;
    customerStages?: string[];
  };
  createdAt: string;
  updatedAt: string;
  isSystemCode: boolean;
}

interface SettingsTabProps {
  language: string;
}

type SubTab = 'discounts' | 'general' | 'notifications';

const SettingsTab: React.FC<SettingsTabProps> = ({ language }) => {
  const { t } = useTranslation('admin');
  const [subTab, setSubTab] = useState<SubTab>('discounts');
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);

  // language prop kept for backward compatibility (date formatting)

  const loadDiscountCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authFetch(`${getApiBaseUrl()}/getDiscountCodes?includeInactive=true`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load discount codes');
      }

      setDiscountCodes(data.codes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiscountCodes();
  }, [loadDiscountCodes]);

  const formatDiscount = (code: DiscountCode) => {
    if (code.discountType === 'percentage') {
      return `${(code.discountValue * 100).toFixed(0)}%`;
    }
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(code.discountValue);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleToggleActive = async (code: DiscountCode) => {
    if (code.isSystemCode) return;
    
    try {
      const response = await authFetch(`${getApiBaseUrl()}/manageDiscountCode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.code,
          isActive: !code.isActive,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update');
      }

      // Reload to get fresh data
      loadDiscountCodes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleDelete = async (code: DiscountCode) => {
    if (code.isSystemCode) return;
    
    const confirmed = window.confirm(
      t('settings.confirmDelete', { code: code.code })
    );
    
    if (!confirmed) return;
    
    try {
      const response = await authFetch(`${getApiBaseUrl()}/manageDiscountCode?code=${code.code}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete');
      }

      loadDiscountCodes();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <div className="settings-tab">
      {/* Sub-tab Navigation */}
      <div className="subtab-navigation">
        <button 
          className={subTab === 'discounts' ? 'active' : ''} 
          onClick={() => setSubTab('discounts')}
        >
          🏷️ {t('settings.discountCodes')}
        </button>
        <button 
          className={subTab === 'general' ? 'active' : ''} 
          onClick={() => setSubTab('general')}
        >
          ⚙️ {t('settings.general')}
        </button>
        <button 
          className={subTab === 'notifications' ? 'active' : ''} 
          onClick={() => setSubTab('notifications')}
        >
          🔔 {t('settings.notifications')}
        </button>
      </div>

      {/* Discount Codes Tab */}
      {subTab === 'discounts' && (
        <div className="discounts-section">
          <div className="section-header">
            <h3>🏷️ {t('settings.discountCodes')}</h3>
            <button 
              className="create-btn"
              onClick={() => setShowCreateModal(true)}
            >
              ➕ {t('settings.createCode')}
            </button>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-message">
              ⚠️ {error}
              <button onClick={loadDiscountCodes}>{t('settings.retry')}</button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{t('settings.loading')}</p>
            </div>
          )}

          {/* Discount Codes List */}
          {!loading && discountCodes.length > 0 && (
            <div className="codes-table-container">
              <table className="codes-table">
                <thead>
                  <tr>
                    <th>{t('settings.code')}</th>
                    <th>{t('settings.discount')}</th>
                    <th>{t('settings.used')}</th>
                    <th>{t('settings.status')}</th>
                    <th>{t('settings.expires')}</th>
                    <th>{t('settings.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {discountCodes.map((code) => (
                    <tr key={code.code} className={!code.isActive ? 'inactive' : ''}>
                      <td>
                        <div className="code-cell">
                          <span className="code-value">{code.code}</span>
                          {code.isSystemCode && (
                            <span className="system-badge">{t('settings.system')}</span>
                          )}
                        </div>
                        <div className="code-description">{code.description}</div>
                      </td>
                      <td>
                        <span className="discount-value">{formatDiscount(code)}</span>
                        <span className="discount-type">
                          {code.discountType === 'percentage' ? t('settings.off') : t('settings.fixed')}
                        </span>
                      </td>
                      <td>
                        <span className="usage-count">{code.usedCount}</span>
                        {code.maxUses && (
                          <span className="usage-max"> / {code.maxUses}</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${code.isActive ? 'active' : 'inactive'}`}>
                          {code.isActive ? t('settings.active') : t('settings.inactive')}
                        </span>
                      </td>
                      <td>{formatDate(code.expiresAt)}</td>
                      <td className="actions-cell">
                        {!code.isSystemCode && (
                          <>
                            <button 
                              className="action-btn toggle"
                              onClick={() => handleToggleActive(code)}
                              title={code.isActive ? t('settings.deactivate') : t('settings.activate')}
                            >
                              {code.isActive ? '⏸️' : '▶️'}
                            </button>
                            <button 
                              className="action-btn edit"
                              onClick={() => setEditingCode(code)}
                              title={t('settings.edit')}
                            >
                              ✏️
                            </button>
                            <button 
                              className="action-btn delete"
                              onClick={() => handleDelete(code)}
                              title={t('settings.delete')}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                        {code.isSystemCode && (
                          <span className="system-note">{t('settings.readOnly')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* General Settings Tab (Placeholder) */}
      {subTab === 'general' && (
        <div className="general-section">
          <div className="placeholder-content">
            <span className="placeholder-icon">⚙️</span>
            <h3>{t('settings.placeholders.generalTitle')}</h3>
            <p>{t('settings.placeholders.comingSoon')}</p>
            <ul>
              <li>{t('settings.placeholders.defaultLanguage')}</li>
              <li>{t('settings.placeholders.currencySettings')}</li>
              <li>{t('settings.placeholders.inventoryThresholds')}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Notifications Settings Tab */}
      {subTab === 'notifications' && (
        <PushNotificationsTab language={language} />
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCode) && (
        <DiscountCodeModal
          language={language}
          code={editingCode}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCode(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingCode(null);
            loadDiscountCodes();
          }}
        />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Discount Code Modal Component
// ──────────────────────────────────────────────────────────────────────────────
interface DiscountCodeModalProps {
  language: string;
  code: DiscountCode | null;
  onClose: () => void;
  onSave: () => void;
}

const DiscountCodeModal: React.FC<DiscountCodeModalProps> = ({ 
  language, 
  code, 
  onClose, 
  onSave 
}) => {
  const isEdit = !!code;
  const { t } = useTranslation('admin');
  // language prop kept for backward compatibility

  const [formData, setFormData] = useState({
    code: code?.code || '',
    description: code?.description || '',
    discountType: code?.discountType || 'percentage' as 'percentage' | 'fixed',
    discountValue: code?.discountValue || 0.1,
    minOrderValue: code?.minOrderValue || 0,
    maxUses: code?.maxUses || undefined as number | undefined,
    expiresAt: code?.expiresAt?.split('T')[0] || '',
    isActive: code?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        description: formData.description,
        discountType: formData.discountType,
        discountValue: formData.discountType === 'percentage' 
          ? Math.min(formData.discountValue, 1) 
          : formData.discountValue,
        minOrderValue: formData.minOrderValue || undefined,
        maxUses: formData.maxUses || undefined,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : undefined,
        isActive: formData.isActive,
      };

      const response = await authFetch(`${getApiBaseUrl()}/manageDiscountCode`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save');
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? t('settings.modal.editTitle') : t('settings.modal.createTitle')}</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('settings.modal.codeLabel')} *</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="SUMMER20"
              disabled={isEdit}
              required
              pattern="[A-Z0-9]{3,20}"
              title={t('settings.modal.codePattern')}
            />
          </div>

          <div className="form-group">
            <label>{t('settings.modal.descriptionLabel')}</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('settings.modal.descriptionPlaceholder')}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('settings.modal.discountTypeLabel')} *</label>
              <select
                value={formData.discountType}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  discountType: e.target.value as 'percentage' | 'fixed',
                  discountValue: e.target.value === 'percentage' ? 0.1 : 5000,
                }))}
              >
                <option value="percentage">{t('settings.modal.percentageOption')}</option>
                <option value="fixed">{t('settings.modal.fixedOption')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('settings.modal.valueLabel')} *</label>
              {formData.discountType === 'percentage' ? (
                <input
                  type="number"
                  value={formData.discountValue * 100}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    discountValue: parseFloat(e.target.value) / 100 
                  }))}
                  min="1"
                  max="100"
                  step="1"
                  required
                />
              ) : (
                <input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    discountValue: parseFloat(e.target.value) 
                  }))}
                  min="100"
                  step="100"
                  required
                />
              )}
              <span className="input-hint">
                {formData.discountType === 'percentage' ? '%' : '₩'}
              </span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('settings.modal.minOrderLabel')}</label>
              <input
                type="number"
                value={formData.minOrderValue || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  minOrderValue: e.target.value ? parseInt(e.target.value) : 0 
                }))}
                min="0"
                step="1000"
                placeholder="0"
              />
            </div>

            <div className="form-group">
              <label>{t('settings.modal.maxUsesLabel')}</label>
              <input
                type="number"
                value={formData.maxUses || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  maxUses: e.target.value ? parseInt(e.target.value) : undefined 
                }))}
                min="1"
                placeholder={t('settings.modal.unlimitedPlaceholder')}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('settings.modal.expiresLabel')}</label>
              <input
                type="date"
                value={formData.expiresAt}
                onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                {t('settings.modal.activeLabel')}
              </label>
            </div>
          </div>

          {error && (
            <div className="form-error">⚠️ {error}</div>
          )}

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              {t('settings.modal.cancel')}
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving 
                ? t('settings.modal.saving') 
                : isEdit 
                  ? t('settings.modal.update') 
                  : t('settings.modal.create')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsTab;
